import React, { useState, useEffect } from 'react';
import { Modal, Form, Table, Alert, Spinner } from 'react-bootstrap';
import StatisticheCorsiRegioneService from '../../services/StatisticheCorsiRegioneService/StatisticheCorsiRegioneService';
import { TIPO_CORSO } from '../../model/tipoCorso';
import './StatisticheRegioneModal.css';

const ANNO_MIN = 2020;

function buildAnniOptions() {
    const currentYear = new Date().getFullYear();
    const out = [];
    for (let y = ANNO_MIN; y <= currentYear; y++) {
        out.push(y);
    }
    return out;
}

function defaultAnnoStatistica() {
    return new Date().getFullYear() - 1;
}

function getHighlightCol(meta, corso) {
    const c = (meta?.corso_filtro?.codice || corso?.codice || '')
        .toString()
        .trim()
        .toUpperCase();
    if (c === 'ASC90A' || c === 'PAT16A') {
        return c;
    }
    return null;
}

function cellHighlightClass(highlightCol, colKey) {
    const base = 'text-end';
    if (highlightCol === colKey) {
        return `${base} table-info`;
    }
    return base;
}

const COLGROUP = (
    <colgroup>
        <col style={{ width: '50%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
    </colgroup>
);

function IndicatoreSplitTable({ title, descrizione, className, rows, highlightCol, tableClassName = 'mb-3' }) {
    return (
        <>
            <h6 className={className || 'mt-3'}>{title}</h6>
            {descrizione ? <p className="small text-muted mb-2">{descrizione}</p> : null}
            <div className="statistiche-regione-table-wrap">
                <Table bordered size="sm" className={`statistiche-regione-table ${tableClassName}`.trim()}>
                    {COLGROUP}
                    <thead>
                        <tr>
                            <th>Indicatore</th>
                            <th className={highlightCol === 'ASC90A' ? 'text-end table-info' : 'text-end'}>ASC90A</th>
                            <th className={highlightCol === 'PAT16A' ? 'text-end table-info' : 'text-end'}>PAT16A</th>
                            <th className="text-end">Totale</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.label}>
                                <th scope="row">{r.label}</th>
                                <td className={cellHighlightClass(highlightCol, 'ASC90A')}>{r.data?.ASC90A ?? 0}</td>
                                <td className={cellHighlightClass(highlightCol, 'PAT16A')}>{r.data?.PAT16A ?? 0}</td>
                                <td className="text-end">{r.data?.totale ?? 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </>
    );
}

/**
 * Rendicontazione Regione: Mod. D1_A, D2_B/D3_B, D2_A, D3_A (FAD, corso in modale).
 */
export default function StatisticheRegioneModal({ show, onHide, corso }) {
    const [anno, setAnno] = useState(defaultAnnoStatistica);
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const anniOptions = buildAnniOptions();

    const idCorso = corso?.id_corso != null ? Number(corso.id_corso) : null;

    useEffect(() => {
        if (!show || !idCorso) {
            setDati(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        StatisticheCorsiRegioneService.getRendicontazioneAnno({
            anno_statistico: anno,
            id_corso: idCorso,
            solo_corsi_asincroni: corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA,
        })
            .then((res) => {
                if (!cancelled) {
                    const root = res?.data != null && typeof res.data === 'object' ? res.data : res;
                    setDati(root);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Impossibile caricare le statistiche.');
                    setDati(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [show, idCorso, anno, corso?.modalita_erogazione, corso?.codice]);

    const meta = dati?.meta;
    const d1a = dati?.d1_a_numero_allievi_effettivi;
    const d2b = dati?.d2_b_avviato_percorso_formativo;
    const d2a = dati?.d2_a_concluso_percorso_formativo;
    const d3a = dati?.d3_a_con_esame_promosso;
    const highlightCol = getHighlightCol(meta, corso);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="text-break">
                    <i className="bi bi-clipboard2-pulse me-2" aria-hidden="true" />
                    Rendicontazione Regione
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {corso && (
                    <p className="text-muted small mb-2">
                        <strong>{corso.denominazione}</strong>
                        {corso.codice ? ` · ${corso.codice}` : ''}
                    </p>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Anno di rendicontazione (anno solare)</Form.Label>
                    <Form.Select
                        value={anno}
                        onChange={(e) => setAnno(parseInt(e.target.value, 10))}
                        disabled={loading}
                    >
                        {anniOptions.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {meta?.nota && (
                    <Alert variant="info" className="small py-2">
                        {meta.nota}
                    </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Caricamento...
                    </div>
                ) : dati && !error ? (
                    <>
                        {meta && (
                            <div className="small text-muted mb-3">
                                <div>
                                    <strong>Coorte</strong>: iscrizioni (
                                    <code className="small">data_inserimento</code> su classi_utenti) tra{' '}
                                    <strong>{meta.intervallo_anno_solare?.da}</strong> e{' '}
                                    <strong>{meta.intervallo_anno_solare?.a}</strong> (come D1·A; su D2·A/D3·A si
                                    aggiunge la tolleranza in calce).
                                </div>
                                {meta.data_cutoff_regionale_d2_d3_solo_giorno && (
                                    <div>
                                        D2·A e D3·A (chiusure / esame, tolleranza FAD asincrono): conclusi o promossi entro
                                        il{' '}
                                        <strong>{meta.data_cutoff_regionale_d2_d3_solo_giorno}</strong>
                                        {meta.anno_rendicontazione_d2_d3 != null ? (
                                            <>
                                                {' '}
                                                (16 aprile {meta.anno_rendicontazione_d2_d3},
                                                cioè anno successivo all’anno solare selezionato)
                                            </>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )}

                        {meta?.corso_filtro?.codice && (
                            <p className="small text-muted mb-3">
                                Conteggi per: <strong>{meta.corso_filtro.codice}</strong>
                                {meta.corso_filtro.denominazione
                                    ? ` — ${meta.corso_filtro.denominazione}`
                                    : ''}
                                . Nella griglia, la colonna omonima al codice corso è evidenziata; l&apos;altra
                                colonna (ASC90A/PAT16A) è 0.
                            </p>
                        )}

                        <IndicatoreSplitTable
                            title="Numero di allievi effettivi — Mod. D1_A"
                            descrizione={
                                'Iscritti nell’anno solare selezionato. Per iscritto si intende: ' +
                                'utente con abilitato_web = 1, is_prova = 0, e stato_pagamento ' +
                                '“Pagato” o “Parzialmente_pagato” (e filtri FAD ove applicati).'
                            }
                            className="mt-0"
                            highlightCol={highlightCol}
                            rows={[
                                {
                                    label: 'Allievi effettivi (D1_A)',
                                    data: d1a,
                                },
                            ]}
                        />

                        <IndicatoreSplitTable
                            title="Allievi che hanno avviato il percorso formativo — Mod. D2_B / Mod. D3_B"
                            descrizione={
                                'Stessa coorte iscritta nell’anno (come D1·A), con almeno una lettura in quell’anno ' +
                                '(prima data_inizio su un contenuto del corso).'
                            }
                            highlightCol={highlightCol}
                            rows={[
                                {
                                    label: 'Percorso avviato in anno (D2_B / D3_B)',
                                    data: d2b,
                                },
                            ]}
                        />

                        <IndicatoreSplitTable
                            title="Allievi che hanno concluso il percorso formativo — Mod. D2_A"
                            descrizione={
                                'Solo corsisti iscritti nell’anno solare: o tutti gli obbligatori (opzionale = 0) con ' +
                                'completato = 1 e data_inizio entro la cut-off, oppure esame promosso entro la stessa ' +
                                'cut-off (come D3·A), così D2·A include sempre D3·A.'
                            }
                            highlightCol={highlightCol}
                            rows={[
                                {
                                    label: 'Percorso concluso (D2_A)',
                                    data: d2a,
                                },
                            ]}
                        />

                        <IndicatoreSplitTable
                            title="Allievi che hanno concluso con esame idoneo — Mod. D3_A"
                            descrizione={
                                'Solo corsisti iscritti nell’anno solare; esame “promosso” con data entro la tolleranza ' +
                                '16/04 (anno renda = anno solare + 1), coerente con D2·A per la dispersione.'
                            }
                            className="mb-0"
                            tableClassName="mb-0"
                            highlightCol={highlightCol}
                            rows={[
                                {
                                    label: 'Esame promosso entro cut-off regionale (D3_A)',
                                    data: d3a,
                                },
                            ]}
                        />
                    </>
                ) : null}
            </Modal.Body>
        </Modal>
    );
}
